import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile } from "@fortawesome/free-regular-svg-icons";

interface ThumbImageProps {
  src: string;
  alt: string;
  /** Formato do arquivo, mostrado no placeholder quando a miniatura falta. */
  label: string;
  className?: string;
  width?: number;
  height?: number;
  compact?: boolean;
}

/**
 * Miniatura do gerenciador com placeholder neutro (F-GC.3).
 *
 * O card nunca refaz render: sem miniatura durável e sem pirâmide no disco a
 * rota responde 404, e aqui isso vira ícone + formato em vez de imagem
 * quebrada. A mesma URL não é pedida de novo; uma URL nova (o render refeito
 * pela abertura do viewer) tenta uma vez.
 */
export function ThumbImage({ src, alt, label, className, width, height, compact = false }: ThumbImageProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  if (failedSrc === src) {
    return <ThumbPlaceholder label={label} compact={compact} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
      width={width}
      height={height}
      onError={() => setFailedSrc(src)}
    />
  );
}

/** Ícone de arquivo + formato; sem formato, o texto diz que a miniatura falta. */
export function ThumbPlaceholder({ label, compact = false }: { label?: string; compact?: boolean }) {
  return (
    <div
      role="img"
      aria-label={label ? `Miniatura indisponível (${label})` : "Miniatura indisponível"}
      title="Miniatura indisponível"
      className="flex h-full w-full flex-col items-center justify-center gap-1 text-ink-600"
    >
      <FontAwesomeIcon icon={faFile} className={compact ? "h-3 w-3" : "h-8 w-8"} />
      <span className={compact ? "text-[8px] leading-none" : "text-[10px]"}>
        {label ?? "Miniatura indisponível"}
      </span>
    </div>
  );
}
